import { Button, DataTable, PageLoading, WarningIcon } from '@wonderschool/common-base-ui';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EnrollmentStatusEnum } from '../../../enrollments/enums';
import { logError } from '../../../rollbar';
import { useStudents } from '../../../students/studentsHooks';
import { StudentMappingModal } from './StudentMappingModal';

export const KinderConnectStudentMappings: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const studentsFilter = {
    searchText: '',
    filters: {
      enrollmentStatus: EnrollmentStatusEnum.ENROLLED,
      // An archived student can still read as enrolled (the unenroll cron skips
      // archived records), and must not be offered for external mapping edits.
      hideArchivedStudents: true,
    },
  };

  const { listFiltered: students } = useStudents(studentsFilter);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
    } catch (err) {
      logError('Error fetching students: ', err);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      fieldName: 'displayName',
      label: t('common.name'),
    },
    {
      fieldName: 'kinderSystems.kinderConnectId',
      label: t('apps.kinderConnect.studentMappings.idLabel'),
      renderCell: (student) =>
        student.kinderSystems?.kinderConnectId || (
          <span className="flex w-32 rounded-full bg-red-200 px-2 py-2 text-sm">
            <WarningIcon className="mr-1 h-5 w-5 text-red-700" />
            {t('apps.kinderConnect.noIdFound')}
          </span>
        ),
    },
    {
      fieldName: 'actions',
      label: t('Actions'),
      renderCell: (student) => <Button onClick={() => handleEditMapping(student)}>{t('common.edit')}</Button>,
    },
  ];

  const handleEditMapping = (student: any) => {
    setSelectedStudent(student);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStudent(null);
  };

  if (loading) return <PageLoading />;

  if (!students.length) {
    return <p className="text-gray-600">{t('apps.kinderConnect.studentMappings.empty')}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <DataTable
        data={students}
        columns={columns}
        noResults={<p className="py-4 text-center">{t('No students found')}</p>}
      />
      {selectedStudent && (
        <StudentMappingModal isOpen={isModalOpen} onClose={handleCloseModal} student={selectedStudent} />
      )}
    </div>
  );
};
